import {
  useGetAllExamCentersQuery,
  useGetYearlyExamEventsCountByMonthQuery,
} from '@exam-center/graphql';
import { useNavigate, useParams } from '@exam-center/router';
import { Loading, Table, TableContainer } from '@shared/components';
import { TableBody, TableHeader } from '@shared/table-generator';
import { useMemo } from 'react';

import { useExamDistributionListSearchParams } from '../../hooks';

import { getHeaderWithExamCenterCodes } from './columns';
import { generateRows } from './utils';
export default function ExamDistributionList() {
  const { year } = useParams('/admin/exam-distribution/year/:year');
  const [{ examCenters }] = useExamDistributionListSearchParams();
  const navigate = useNavigate();

  const { data: allExamCentersData, loading: isLoadingAllExamCenters } =
    useGetAllExamCentersQuery();

  const allExamCenterCodes = useMemo(() => {
    return allExamCentersData?.ecGetAllExamCenters.examCenters
      ? [...allExamCentersData.ecGetAllExamCenters.examCenters]
          .sort((a, b) => a.examCenterId - b.examCenterId)
          .map((examCenter) => examCenter.code)
      : [];
  }, [allExamCentersData]);

  const examCenterCodes = useMemo(() => {
    return examCenters?.length
      ? allExamCenterCodes.filter((code) => examCenters.includes(code))
      : allExamCenterCodes;
  }, [allExamCenterCodes, examCenters]);

  const { data: yearlyExamExamEventCountData, loading: isLoadingYearlyExamExamEventCount } =
    useGetYearlyExamEventsCountByMonthQuery({
      variables: {
        getYearlyExamEventsCountByMonthInput: {
          examCenterCodes: examCenters.length > 0 ? examCenters : undefined,
          ictExamYear: parseInt(year),
        },
      },
      skip: !year,
    });

  const isLoading = isLoadingAllExamCenters || isLoadingYearlyExamExamEventCount;

  const rows = useMemo(
    () =>
      generateRows(
        year,
        examCenterCodes,
        yearlyExamExamEventCountData?.ecGetYearlyExamEventsCountByMonth.examEventsCountByMonth ??
          [],
      ),
    [year, examCenterCodes, yearlyExamExamEventCountData],
  );

  const columns = useMemo(() => getHeaderWithExamCenterCodes(examCenterCodes), [examCenterCodes]);

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const month = event.currentTarget.dataset.monthAbbr ?? '';
    navigate('/admin/exam-distribution/year/:year/month/:month/exam-assigning', {
      params: { year, month },
    });
  };

  if (isLoading) {
    return <Loading height='100%' />;
  }

  return (
    <TableContainer
      sx={(theme) => ({
        borderRadius: theme.shape.rounded(2),
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        width: '100%',
      })}
    >
      <Table sx={{ borderCollapse: 'collapse', tableLayout: 'auto' }}>
        <TableHeader
          slotProps={{
            tableHeaderCell: {
              sx: (theme) => ({
                padding: theme.spacing(3),
                borderBottom: `1px solid ${theme.palette.grey[300]}`,
                borderRight: `1px solid ${theme.palette.grey[300]}`,
                backgroundColor: theme.palette.grey[100],
                '&:last-child': {
                  borderRight: 'none',
                },
                ...theme.typography.body1Semibold,
              }),
            },
          }}
          columns={columns}
        />
        <TableBody
          slotProps={{
            tableBodyCell: {
              sx: (theme) => ({
                padding: theme.spacing(3),
                borderBottom: `1px solid ${theme.palette.grey[300]}`,
                borderRight: `1px solid ${theme.palette.grey[300]}`,
                '&:last-child': {
                  borderRight: 'none',
                },
                ...theme.typography.body1,
              }),
            },
            tableBodyRow: {
              onClick: handleRowClick,
              sx: (theme) => ({
                cursor: 'pointer',
                ':last-of-type > td': {
                  borderBottom: 'none',
                },
                ':hover': {
                  backgroundColor: theme.palette.grey[100],
                },
              }),
            },
          }}
          rows={rows}
        />
      </Table>
    </TableContainer>
  );
}
